import "./MetaDocPdfViewer.scss";

import LoadingProgress from "components/Loading/LoadingProgress";
import debounce from "lodash/debounce";
import { useEffect, useMemo, useRef, useState } from "react";
import { pdfjs } from "react-pdf";

import * as Sentry from "@sentry/react";
import { useVirtualizer } from "@tanstack/react-virtual";

import PdfPageRenderer from "./PdfPageRenderer";

import type { PDFDocumentProxy } from "pdfjs-dist";
import type { MetadocSearchResult } from "services/metadoc-search-api";
import type { PageState } from "../../metadoc.types";

// configure PDF.js worker
if (
  import.meta.env.VITE_APP_ENV === "local" &&
  !import.meta.env.VITE_USES_BACKEND
) {
  // local vite dev server (port 3001): use file from public directory (copied by dev script)
  pdfjs.GlobalWorkerOptions.workerSrc = `/pdf.worker.mjs`;
} else {
  // local watch mode (port 8000) & deployed environments: use static copy from build assets
  const pdfjsVersion = pdfjs.version;
  const workerPath = `pdfjs-build/${pdfjsVersion}/pdf.worker.mjs`;
  pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    workerPath,
    import.meta.url,
  ).toString();
}

type MetaDocPdfViewerProps = {
  metadocId: number;
  downloadUrl: string;
  zoomPercent: number;
  currentPage: number;
  onCurrentPageChange: (page: number) => void;
  onPdfDocLoad?: (pdfDoc: PDFDocumentProxy) => void;
  onLoadingChange?: (isLoading: boolean) => void;
  pageStates?: Map<number, PageState>;
  disableScrollTracking?: boolean;
  searchHighlightsByPage?: Map<number, MetadocSearchResult[]>;
  visiblePageNumbers?: number[];
};

const ZOOM_SYNC_THROTTLE_MS = 120;

const MetaDocPdfViewer = ({
  metadocId,
  downloadUrl,
  zoomPercent,
  currentPage,
  onCurrentPageChange,
  onPdfDocLoad,
  onLoadingChange,
  pageStates,
  disableScrollTracking = false,
  searchHighlightsByPage,
  visiblePageNumbers,
}: MetaDocPdfViewerProps) => {
  const [pdfDoc, setPdfDoc] = useState<PDFDocumentProxy | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(800);
  const [pageDimensions, setPageDimensions] = useState<
    Map<number, { width: number; height: number }>
  >(new Map());
  const [defaultPageSize, setDefaultPageSize] = useState<{
    width: number;
    height: number;
  } | null>(null);

  const parentRef = useRef<HTMLDivElement>(null);
  const scrollingRef = useRef(false);
  const isUserScrollingRef = useRef(false);
  const previousZoomPercentRef = useRef(zoomPercent);
  const zoomSyncTimeoutRef = useRef<number | null>(null);
  const lastZoomSyncAtRef = useRef(0);

  const PDF_HORIZONTAL_PADDING_PX = 32;

  // fit PDF to container width with padding (scaled down to 85% for better fit)
  const calculateScale = (pageWidth: number): number => {
    const zoomMultiplier = zoomPercent / 100;
    const effectiveWidth = Math.max(containerWidth, 400);
    return ((effectiveWidth - 40) / pageWidth) * 0.85 * zoomMultiplier;
  };

  // load PDF
  useEffect(() => {
    if (!downloadUrl) return;

    const loadPdf = async () => {
      try {
        setIsLoading(true);
        if (onLoadingChange) {
          onLoadingChange(true);
        }
        setError(null);

        // use presigned S3 URL directly (requires S3 CORS to allow port 3001)
        const response = await fetch(downloadUrl);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const arrayBuffer = await response.arrayBuffer();

        const loadingTask = pdfjs.getDocument({ data: arrayBuffer });
        const pdf = await loadingTask.promise;

        // pre-load ALL page dimensions upfront for accurate virtualization
        // this eliminates estimation errors and prevents layout shifts/gaps
        const dimensionsMap = new Map<
          number,
          { width: number; height: number }
        >();

        try {
          // fetch dimensions for all pages in parallel (fast - just viewport calculations)
          // for very large PDFs (500+ pages), add timeout to prevent hanging
          const dimensionPromises = Array.from(
            { length: pdf.numPages },
            async (_, i) => {
              const pageNum = i + 1;
              try {
                const page = await pdf.getPage(pageNum);
                const viewport = page.getViewport({ scale: 1.0 });
                return {
                  pageNum,
                  width: viewport.width,
                  height: viewport.height,
                };
              } catch (e) {
                return null;
              }
            },
          );

          // add timeout for large PDFs (30 seconds max for dimension loading)
          const timeoutPromise = new Promise<null>((resolve) =>
            setTimeout(() => resolve(null), 30000),
          );

          let results = await Promise.race([
            Promise.all(dimensionPromises),
            timeoutPromise,
          ]);

          // if timeout occurred, results will be null
          if (!results) {
            // log timeout for monitoring
            Sentry.captureMessage("PDF dimension loading timeout", {
              level: "warning",
              tags: { feature: "metadoc", action: "pdf_dimension_loading" },
              extra: { metadocId, pageCount: pdf.numPages },
            });
            // continue with fallback dimensions
            results = await Promise.all(dimensionPromises);
          }

          // populate dimensions map (results could be null if timeout occurred)
          if (results && Array.isArray(results)) {
            results.forEach((result) => {
              if (result) {
                dimensionsMap.set(result.pageNum, {
                  width: result.width,
                  height: result.height,
                });
              }
            });
          }

          // set default page size from page 1
          const page1Dims = dimensionsMap.get(1);
          if (page1Dims) {
            setDefaultPageSize(page1Dims);
          }

          // set all page dimensions at once
          setPageDimensions(dimensionsMap);
        } catch (e) {
          // failed to pre-load dimensions, will use fallback
        }

        setPdfDoc(pdf);
        setIsLoading(false);
        if (onLoadingChange) {
          onLoadingChange(false);
        }

        // notify parent component that PDF is loaded
        if (onPdfDocLoad) {
          onPdfDocLoad(pdf);
        }
      } catch (err) {
        // log to Sentry for monitoring
        Sentry.captureException(err, {
          tags: { feature: "metadoc", action: "pdf_loading" },
          extra: { metadocId, downloadUrl },
        });
        setError(err instanceof Error ? err.message : "Failed to load PDF");
        setIsLoading(false);
        if (onLoadingChange) {
          onLoadingChange(false);
        }
      }
    };

    loadPdf();
  }, [downloadUrl, metadocId, onPdfDocLoad, onLoadingChange]);

  // measure container width for fit-to-width
  useEffect(() => {
    if (!parentRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(parentRef.current);
    setContainerWidth(parentRef.current.offsetWidth);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // estimate page height for virtualizer
  // NOTE: virtualizer passes 0-based index, but our pages are 1-based
  const estimatePageHeight = (index: number): number => {
    const gapBetweenPages = 8; // padding-bottom for visual separation
    const pageNum = visiblePageNumbers?.[index] ?? index + 1;

    // calculate from pre-loaded dimensions (should always be available after load)
    const dimensions = pageDimensions.get(pageNum) || defaultPageSize;
    if (dimensions) {
      const scale = calculateScale(dimensions.width);
      const height = dimensions.height * scale + gapBetweenPages;
      return Math.max(height, 100);
    }

    // fallback (should rarely be used since we pre-load all dimensions)
    return 900 + gapBetweenPages;
  };

  const effectiveWidth = Math.max(containerWidth, 400);
  const zoomMultiplier = zoomPercent / 100;
  const estimatedRenderedPageWidth = Math.max(
    (effectiveWidth - 40) * 0.85 * zoomMultiplier,
    0,
  );
  const estimatedTotalPdfWidth =
    estimatedRenderedPageWidth + PDF_HORIZONTAL_PADDING_PX;
  const hasHorizontalOverflow = estimatedTotalPdfWidth > containerWidth;
  const contentWidth = hasHorizontalOverflow
    ? estimatedTotalPdfWidth
    : containerWidth;
  const effectiveVisiblePageNumbers = useMemo(() => {
    if (!pdfDoc) {
      return [];
    }

    if (!visiblePageNumbers) {
      return Array.from({ length: pdfDoc.numPages }, (_, index) => index + 1);
    }

    return visiblePageNumbers.filter(
      (page, index, pages) =>
        page >= 1 &&
        page <= pdfDoc.numPages &&
        (index === 0 || pages[index - 1] !== page),
    );
  }, [pdfDoc, visiblePageNumbers]);
  const visiblePageCount = effectiveVisiblePageNumbers.length;

  // virtualizer configuration
  const virtualizer = useVirtualizer({
    count: visiblePageCount,
    getScrollElement: () => parentRef.current,
    estimateSize: estimatePageHeight,
    // overscan: render additional pages above/below viewport for smooth scrolling
    // higher value = smoother scrolling but more memory usage
    // 10 pages ensures pages are pre-rendered before scrolling into view,
    // preventing blank areas when scrolling up quickly
    overscan: 10,
  });
  const debouncedMeasure = useMemo(
    () => debounce(() => virtualizer.measure(), 50),
    [virtualizer],
  );

  useEffect(
    () => () => {
      debouncedMeasure.cancel();
    },
    [debouncedMeasure],
  );

  useEffect(
    () => () => {
      if (zoomSyncTimeoutRef.current !== null) {
        window.clearTimeout(zoomSyncTimeoutRef.current);
      }
    },
    [],
  );

  // scroll to current page when currentPage changes externally
  useEffect(() => {
    if (!pdfDoc || !effectiveVisiblePageNumbers.includes(currentPage)) {
      return;
    }

    // If the page change was triggered by the user scrolling, don't force scroll back
    if (isUserScrollingRef.current) {
      isUserScrollingRef.current = false;
      return;
    }

    // don't force scroll to page 1 on initial load if we are already there
    // this avoids the "Failed to scroll" error when the list is just initializing
    if (
      currentPage === 1 &&
      (!parentRef.current || parentRef.current.scrollTop === 0)
    ) {
      return;
    }

    scrollingRef.current = true;
    virtualizer.scrollToIndex(
      effectiveVisiblePageNumbers.indexOf(currentPage),
      {
        align: "start",
        behavior: "auto", // smooth scrolling causes issues with virtualization
      },
    );

    // reset scrolling flag after animation
    setTimeout(() => {
      scrollingRef.current = false;
    }, 500);
  }, [currentPage, effectiveVisiblePageNumbers, pdfDoc, virtualizer]);

  // track current page based on scroll position
  useEffect(() => {
    if (scrollingRef.current || !pdfDoc || disableScrollTracking) {
      return;
    }

    const items = virtualizer.getVirtualItems();
    if (items.length > 0) {
      // find the page closest to viewport center
      const viewportCenter = parentRef.current
        ? parentRef.current.scrollTop + parentRef.current.clientHeight / 2
        : 0;

      let closestPage = effectiveVisiblePageNumbers[0] ?? currentPage;
      let closestDistance = Infinity;

      for (const item of items) {
        const itemCenter = item.start + item.size / 2;
        const distance = Math.abs(itemCenter - viewportCenter);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestPage = effectiveVisiblePageNumbers[item.index] ?? closestPage;
        }
      }

      if (closestPage !== currentPage) {
        isUserScrollingRef.current = true;
        onCurrentPageChange(closestPage);
      }
    }
  }, [
    effectiveVisiblePageNumbers,
    virtualizer.getVirtualItems(),
    pdfDoc,
    currentPage,
    onCurrentPageChange,
    disableScrollTracking,
  ]);

  // recalculate virtualizer when PDF loads
  useEffect(() => {
    if (pdfDoc) {
      virtualizer.measure();
    }
  }, [pdfDoc, virtualizer]);

  useEffect(() => {
    if (!pdfDoc) {
      return;
    }

    if (previousZoomPercentRef.current === zoomPercent) {
      return;
    }
    previousZoomPercentRef.current = zoomPercent;

    if (!effectiveVisiblePageNumbers.includes(currentPage)) {
      return;
    }

    const currentPageIndex = effectiveVisiblePageNumbers.indexOf(currentPage);

    const runZoomSync = () => {
      const now = Date.now();
      lastZoomSyncAtRef.current = now;

      scrollingRef.current = true;
      debouncedMeasure();

      requestAnimationFrame(() => {
        virtualizer.scrollToIndex(currentPageIndex, {
          align: "center",
          behavior: "auto",
        });

        if (parentRef.current) {
          const { scrollWidth, clientWidth } = parentRef.current;
          parentRef.current.scrollLeft =
            scrollWidth > clientWidth ? (scrollWidth - clientWidth) / 2 : 0;
        }

        window.setTimeout(() => {
          scrollingRef.current = false;
        }, 200);
      });
    };

    const now = Date.now();
    const elapsedSinceLastSync = now - lastZoomSyncAtRef.current;
    if (elapsedSinceLastSync >= ZOOM_SYNC_THROTTLE_MS) {
      runZoomSync();
      return;
    }

    if (zoomSyncTimeoutRef.current !== null) {
      window.clearTimeout(zoomSyncTimeoutRef.current);
    }

    const waitMs = ZOOM_SYNC_THROTTLE_MS - elapsedSinceLastSync;
    zoomSyncTimeoutRef.current = window.setTimeout(() => {
      zoomSyncTimeoutRef.current = null;
      runZoomSync();
    }, waitMs);
  }, [
    contentWidth,
    currentPage,
    effectiveVisiblePageNumbers,
    pdfDoc,
    virtualizer,
    zoomPercent,
  ]);

  const handleDimensionsCalculated = (
    pageNum: number,
    width: number,
    height: number,
  ) => {
    setPageDimensions((prev) => {
      const newMap = new Map(prev);
      newMap.set(pageNum, { width, height });
      return newMap;
    });
  };

  // remeasure virtualizer when page dimensions are loaded to replace estimates with actual sizes
  useEffect(() => {
    if (pageDimensions.size > 0 || defaultPageSize) {
      virtualizer.measure();
    }
  }, [pageDimensions, defaultPageSize, virtualizer]);

  // NOTE: We intentionally DO NOT remeasure when renderedSizes changes
  // because we pre-load all dimensions upfront. Remeasuring causes layout shifts
  // when scrolling up from the bottom. The pre-loaded dimensions are accurate enough.

  if (isLoading) {
    return (
      <div className="metadoc-pdf-viewer metadoc-pdf-viewer--loading">
        <LoadingProgress
          messages={["Loading PDF document...", "This may take a moment"]}
          isLoading={true}
          position="relative"
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="metadoc-pdf-viewer metadoc-pdf-viewer--error">
        <div className="metadoc-error">
          <span className="material-icons metadoc-error-icon">
            error_outline
          </span>
          <p className="metadoc-error-message">Failed to load PDF</p>
          <p className="metadoc-error-detail">{error}</p>
        </div>
      </div>
    );
  }

  if (!pdfDoc) {
    return null;
  }

  return (
    <div ref={parentRef} className="metadoc-pdf-viewer">
      <div
        className="metadoc-pdf-viewer__content"
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: hasHorizontalOverflow ? `${contentWidth}px` : "100%",
          margin: "0 auto",
          position: "relative",
        }}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const pageNum = effectiveVisiblePageNumbers[virtualRow.index];

          if (!pageNum) {
            return null;
          }

          const dimensions = pageDimensions.get(pageNum);
          const scale = dimensions ? calculateScale(dimensions.width) : 1.0;

          return (
            <div
              key={virtualRow.key}
              data-index={virtualRow.index}
              data-page-number={pageNum}
              className="metadoc-pdf-viewer__page-container"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                transform: `translateY(${virtualRow.start}px)`,
                paddingBottom: "8px", // add visual gap between pages
              }}
            >
              <PdfPageRenderer
                pdfDoc={pdfDoc}
                pageNum={pageNum}
                scale={scale}
                pageState={pageStates?.get(pageNum)}
                onDimensionsCalculated={handleDimensionsCalculated}
                searchHighlights={searchHighlightsByPage?.get(pageNum)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MetaDocPdfViewer;
